import React, { useState } from 'react';
import { View, Text, StyleSheet, Dimensions } from 'react-native';
import { BarChart } from 'react-native-chart-kit';
import Dropdown from '../../Dropdown';
import { Card } from '../../cards';
import colors from '../../../config/colors';

/**
 * PumpStatsChart - Bar chart with metric and period selectors
 * Shows pump statistics over time
 */
function PumpStatsChart({
    data = [],
    labels = [],
    selectedMetric = 'runs',
    selectedPeriod = 7,
    onMetricChange,
    onPeriodChange,
    previousPeriodChange = null, // e.g., -6 for 6% decrease
}) {
    const screenWidth = Dimensions.get('window').width;

    const metricOptions = [
        { label: 'Runs', value: 'runs' },
        { label: 'Gallons', value: 'gallons' },
        { label: 'Avg. Interval', value: 'interval' },
    ];

    const periodOptions = [
        { label: 'Last 7 days', value: 7 },
        { label: 'Last 30 days', value: 30 },
        { label: 'Last 90 days', value: 90 },
    ];

    const chartConfig = {
        backgroundColor: colors.white,
        backgroundGradientFrom: colors.white,
        backgroundGradientTo: colors.white,
        decimalPlaces: 0,
        color: (opacity = 1) => `rgba(255, 165, 0, ${opacity})`, // Orange bars
        labelColor: (opacity = 1) => `rgba(100, 100, 100, ${opacity})`,
        style: {
            borderRadius: 16,
        },
        barPercentage: 0.7,
        propsForBackgroundLines: {
            strokeDasharray: '',
            stroke: '#e0e0e0',
        },
    };

    // Ensure we have at least some data to display
    const chartData = {
        labels: labels.length > 0 ? labels : ['No data'],
        datasets: [
            {
                data: data.length > 0 ? data : [0],
            },
        ],
    };

    const getMetricLabel = () => {
        switch (selectedMetric) {
            case 'runs':
                return 'Runs';
            case 'gallons':
                return 'Gallons';
            case 'interval':
                return 'Avg. Interval';
            default:
                return '';
        }
    };

    const getMetricValue = () => {
        if (data.length === 0) return '0';
        const total = data.reduce((a, b) => a + b, 0);
        if (selectedMetric === 'interval') {
            // Format as time
            const avgSeconds = Math.round(total / data.length);
            const minutes = Math.floor(avgSeconds / 60);
            const seconds = avgSeconds % 60;
            return `${minutes}m ${seconds}s`;
        }
        return total.toFixed(2);
    };

    return (
        <Card style={styles.container}>
            {/* Selector Row */}
            <View style={styles.selectorRow}>
                <View style={styles.selectorHalf}>
                    <Dropdown
                        itemsArray={metricOptions}
                        defaultValueProp={selectedMetric}
                        onChangeItemProp={onMetricChange}
                    />
                </View>
                <View style={styles.selectorHalf}>
                    <Dropdown
                        itemsArray={periodOptions}
                        defaultValueProp={selectedPeriod}
                        onChangeItemProp={onPeriodChange}
                    />
                </View>
            </View>

            {/* Summary Row */}
            <View style={styles.summaryRow}>
                <Text style={styles.summaryValue}>{getMetricValue()}</Text>
                {previousPeriodChange !== null && (
                    <Text style={[
                        styles.changeText,
                        previousPeriodChange < 0 ? styles.changeNegative : styles.changePositive
                    ]}>
                        previous period {previousPeriodChange < 0 ? '↓' : '↑'}{Math.abs(previousPeriodChange)}%
                    </Text>
                )}
            </View>

            {/* Bar Chart */}
            <View style={styles.chartContainer}>
                <BarChart
                    data={chartData}
                    width={screenWidth - 80}
                    height={180}
                    chartConfig={chartConfig}
                    style={styles.chart}
                    showValuesOnTopOfBars={false}
                    fromZero={true}
                    withInnerLines={true}
                    withHorizontalLabels={true}
                />
            </View>
        </Card>
    );
}

const styles = StyleSheet.create({
    container: {
        padding: 12,
    },
    selectorRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    selectorHalf: {
        flex: 1,
        marginHorizontal: 4,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
        paddingHorizontal: 4,
    },
    summaryValue: {
        fontSize: 24,
        fontWeight: 'bold',
        color: colors.primary,
    },
    changeText: {
        fontSize: 14,
    },
    changeNegative: {
        color: '#ff6b6b',
    },
    changePositive: {
        color: '#33cc33',
    },
    chartContainer: {
        alignItems: 'center',
    },
    chart: {
        borderRadius: 8,
    },
});

export default PumpStatsChart;
